import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, Loader2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, assetUrl } from '../lib/api';
import { ImageField } from './FieldEditor';
import { RichTextEditor } from './RichTextEditor';

const EMPTY_POST = {
  title: '', slug: '', excerpt: '', image: '', author: 'intexsopro', category: 'News',
  date: new Date().toISOString().slice(0, 10), body: '', video_url: '', published: true,
};

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/posts?all=true').then((res) => setPosts(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm(EMPTY_POST);
    setEditing('new');
  };

  const openEdit = async (slug) => {
    try {
      const res = await api.get(`/posts/${slug}`);
      setForm({ ...EMPTY_POST, ...res.data });
      setEditing(slug);
    } catch {
      toast.error('Failed to load post');
    }
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await api.post('/admin/posts', form);
        toast.success('Post created');
      } else {
        await api.put(`/admin/posts/${editing}`, form);
        toast.success('Post updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!window.confirm('Delete this post permanently?')) return;
    try {
      await api.delete(`/admin/posts/${slug}`);
      toast.success('Post deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const inputCls = 'w-full h-10 rounded-lg bg-white border border-[#E6E8EE] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B]';

  if (editing !== null) {
    return (
      <div data-testid="admin-blog-editor">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-extrabold text-[#26247B]">{editing === 'new' ? 'New Post' : 'Edit Post'}</h1>
          <div className="flex gap-2">
            <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6E8EE] bg-white px-4 py-2.5 text-sm font-bold text-black/70 hover:bg-[#F6F7F9] transition-colors duration-200">
              <X className="h-4 w-4" /> Cancel
            </button>
            <button
              onClick={save}
              data-testid="admin-blog-save-button"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#2E8B57] text-white px-6 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#257247] disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & Publish
            </button>
          </div>
        </div>

        <div className="mt-5 bg-white border border-[#E6E8EE] rounded-2xl p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-black/70 uppercase">Title *</label>
              <input data-testid="admin-blog-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-black/70 uppercase">Category</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-black/70 uppercase">Author</label>
              <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-black/70 uppercase">Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            <div className="space-y-1.5 flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm font-bold">
                <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4" />
                Published
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">Featured Image</label>
            <ImageField value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">Video URL (optional – YouTube / Vimeo)</label>
            <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." className={inputCls} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">Excerpt</label>
            <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={3} className="w-full rounded-lg bg-white border border-[#E6E8EE] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#26247B]" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-black/70 uppercase">Content</label>
            <p className="text-xs text-black/50 mb-1">Write directly, or paste from Word/Google Docs — formatting (bold, headings, lists, links) is kept automatically.</p>
            <RichTextEditor value={form.body} onChange={(html) => setForm({ ...form, body: html })} placeholder="Write your article…" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog-manager">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#26247B]">News & Blog</h1>
          <p className="mt-1 text-sm text-black/60">Create, edit and delete news posts shown on the website.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="admin-blog-new-button"
          className="inline-flex items-center gap-2 rounded-lg bg-[#26247B] text-white px-5 py-2.5 text-sm font-bold transition-colors duration-200 hover:bg-[#1f1d66]"
        >
          <Plus className="h-4 w-4" /> New Post
        </button>
      </div>

      <div className="mt-6 bg-white border border-[#E6E8EE] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#26247B]" /></div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-[#F6F7F9] text-xs font-bold text-black/60 uppercase">
                <th className="px-5 py-3.5">Post</th>
                <th className="px-5 py-3.5 hidden sm:table-cell">Date</th>
                <th className="px-5 py-3.5 hidden sm:table-cell">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.slug} className="border-t border-[#E6E8EE]">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {post.image && <img src={assetUrl(post.image)} alt="" className="h-11 w-16 object-cover rounded-md shrink-0" />}
                      <span className="text-sm font-bold text-[#26247B] line-clamp-2">{post.title}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-black/70 hidden sm:table-cell whitespace-nowrap">{post.date}</td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${post.published ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-black/10 text-black/60'}`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(post.slug)} data-testid="admin-blog-edit-button" className="p-2 rounded-lg hover:bg-[#F6F7F9] text-[#26247B] transition-colors duration-200" aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(post.slug)} data-testid="admin-blog-delete-button" className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors duration-200" aria-label="Delete">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {posts.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-12 text-center text-sm text-black/60">No posts yet. Click “New Post” to create one.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
